<template>
    <DialogRoot
        :open="open"
        @update:open="updateOpen"
    >
        <DialogPortal>
            <DialogOverlay
                :class="element('scrim')"
                @click="dismiss"
            />

            <DialogContent
                :class="block(modifier(`width-${width}`))"
                aria-modal="true"
                @escape-key-down="preventDismiss"
                @pointer-down-outside="preventDismiss"
            >
                <header :class="element('header')">
                    <div :class="element('heading')">
                        <DialogTitle :class="element('title')">
                            <slot name="title">
                                {{ title }}
                            </slot>
                        </DialogTitle>
                        <DialogDescription :class="element('description')">
                            <slot name="description">
                                {{ description }}
                            </slot>
                        </DialogDescription>
                    </div>

                    <DialogClose
                        :class="element('close')"
                        :aria-label="closeLabel"
                    >
                        <span aria-hidden="true">&times;</span>
                    </DialogClose>
                </header>

                <div :class="element('body')">
                    <slot :close="close" />
                </div>

                <footer
                    v-if="$slots.footer"
                    :class="element('footer')"
                >
                    <slot
                        name="footer"
                        :close="close"
                    />
                </footer>
            </DialogContent>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import type { KDialogProps } from './types'
import useBem from '@yeeroen/use-bem'
import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
} from 'reka-ui'

defineOptions({ name: 'KDialog' })
const props = withDefaults(defineProps<KDialogProps>(), {
    open: false,
    description: undefined,
    closeLabel: 'Close dialog',
    dismissible: true,
    width: 'md',
})
const emit = defineEmits<{
    'update:open': [open: boolean]
}>()
defineSlots<{
    default?: (props: { close: () => void }) => unknown
    title?: () => unknown
    description?: () => unknown
    footer?: (props: { close: () => void }) => unknown
}>()
const { block, element, modifier } = useBem('k-dialog')

function updateOpen(open: boolean) {
    if (open || props.dismissible)
        emit('update:open', open)
}

function close() {
    emit('update:open', false)
}

function dismiss() {
    if (props.dismissible)
        close()
}

function preventDismiss(event: Event) {
    if (!props.dismissible)
        event.preventDefault()
}
</script>

<style lang="scss">
@use './k-dialog.scss';
</style>
